/* =============================================================================
   DAILE Mono — search suggestions (catalog group, 2026-10-06). No dependencies; runs once per page.

   Enhances every Mono search form without changing its markup:
   - the header search panel (mn-header: [data-mn-search] form .mn-search__form) — suggestions drop in a block
     under the field, inside the panel;
   - the phone menu field (.mn-menu__search) — block under the field, inside the menu;
   - forms marked [data-mn-suggest] (the search page) — floating dropdown under the field.
   ARIA 1.2 combobox: the field gets role="combobox", aria-controls / aria-expanded / aria-activedescendant;
   options are links (role="option", tabindex -1), focus stays in the field. Arrow keys move through the options
   (wrapping), Enter opens the highlighted one or submits the search, Escape hides the suggestions (the header
   panel closes itself, assets/mn.js), click outside / Tab away hide them too. The number of results is announced
   in a polite live region.

   Data: sections/mn-cat-suggest.liquid through the Section Rendering API, from 2 characters, 200 ms after the
   last keystroke; stale requests are aborted, late answers ignored, answers cached per query:
     predictive — {form action}/suggest?q=…&resources[type]=product,collection,article,page&resources[limit]=10
                  &resources[limit_scope]=each&resources[options][unavailable_products]=last&section_id=mn-cat-suggest
     search     — {form action}?q=…&options[prefix]=last&type=product,article,page&section_id=mn-cat-suggest
   Arabic (<html lang="ar">) starts in "search" mode — Predictive Search answers 417 "Unsupported buyer locale";
   any 417 switches to it for the rest of the visit. Network trouble = no suggestions, Enter still searches.
   Links inside the suggestions get the preview ?view=mono from assets/mn.js (MutationObserver).
   Styles: assets/mn-cat-suggest.css.
   ============================================================================= */
(() => {
  'use strict';

  if (window.__mnSuggest) return;
  window.__mnSuggest = true;

  const d = document;
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 200;
  const CACHE_SIZE = 40;
  const SECTION = 'mn-cat-suggest';
  const PREDICTIVE = [
    'resources%5Btype%5D=product%2Ccollection%2Carticle%2Cpage',
    'resources%5Blimit%5D=10',
    'resources%5Blimit_scope%5D=each',
    'resources%5Boptions%5D%5Bunavailable_products%5D=last',
  ].join('&');
  const FULL = 'options%5Bprefix%5D=last&type=product%2Carticle%2Cpage';
  const SELECTOR = 'form[data-mn-suggest], [data-mn-search] form, form.mn-menu__search';

  let mode = /^ar\b/i.test(d.documentElement.lang || '') ? 'search' : 'predictive';
  const cache = new Map();
  let uid = 0;

  const enhance = (form) => {
    if (form.hasAttribute('data-mn-ps-ready')) return;
    const input = form.querySelector('input[name="q"]');
    if (!input) return;
    form.setAttribute('data-mn-ps-ready', '');

    uid += 1;
    const id = `mn-ps-${uid}`;
    const floating = form.hasAttribute('data-mn-suggest');
    const base = (form.getAttribute('action') || '/search').split('?')[0].split('#')[0].replace(/\/$/, '');

    const panel = d.createElement('div');
    panel.className = `mn-ps-panel ${floating ? 'mn-ps-panel--float' : 'mn-ps-panel--flow'}`;
    panel.hidden = true;
    const inner = d.createElement('div');
    inner.className = 'mn-ps-panel__inner';
    if (!floating && form.classList.contains('mn-container')) inner.classList.add('mn-container');
    const listbox = d.createElement('div');
    listbox.id = `${id}-list`;
    listbox.className = 'mn-ps-panel__list';
    listbox.setAttribute('role', 'listbox');
    const label = form.querySelector('label');
    listbox.setAttribute('aria-label', (label && label.textContent.trim()) || input.getAttribute('placeholder') || 'Search');
    const empty = d.createElement('div');
    empty.className = 'mn-ps-panel__empty';
    empty.hidden = true;
    const status = d.createElement('span');
    status.className = 'mn-sr-only';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    inner.append(listbox, empty);
    panel.append(inner, status);
    if (floating) form.appendChild(panel);
    else form.insertAdjacentElement('afterend', panel);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', listbox.id);
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('autocomplete', 'off');

    let options = [];
    let active = -1;
    let timer = 0;
    let controller = null;
    let frame = 0;

    const setActive = (index, scroll = true) => {
      const prev = options[active];
      if (prev) prev.setAttribute('aria-selected', 'false');
      const next = options[index];
      if (!next) {
        active = -1;
        input.removeAttribute('aria-activedescendant');
        return;
      }
      active = index;
      next.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', next.id);
      if (scroll) next.scrollIntoView({ block: 'nearest' });
    };

    const announce = (text) => {
      if (frame) cancelAnimationFrame(frame);
      status.textContent = '';
      frame = requestAnimationFrame(() => {
        frame = 0;
        status.textContent = text;
      });
    };

    const hide = () => {
      setActive(-1);
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
    };

    const clear = () => {
      hide();
      options = [];
      listbox.replaceChildren();
      empty.replaceChildren();
      empty.hidden = true;
      status.textContent = '';
    };

    const render = (data, query) => {
      if (input.value.trim() !== query) return;
      panel.classList.remove('is-loading');
      setActive(-1);
      const node = d.importNode(data.node, true);
      // Unique ids per field (the header, the menu and the page can show suggestions on the same page).
      node.querySelectorAll('[id]').forEach((el) => {
        const old = el.id;
        el.id = `${id}-${old}`;
        node.querySelectorAll(`[aria-labelledby="${old}"]`).forEach((ref) => ref.setAttribute('aria-labelledby', el.id));
      });
      if (data.count > 0) {
        empty.replaceChildren();
        empty.hidden = true;
        listbox.replaceChildren(node);
        listbox.hidden = false;
        options = Array.from(listbox.querySelectorAll('[role="option"]'));
        input.setAttribute('aria-expanded', 'true');
      } else {
        options = [];
        listbox.replaceChildren();
        listbox.hidden = true;
        empty.replaceChildren(node);
        empty.hidden = false;
        input.setAttribute('aria-expanded', 'false');
      }
      panel.hidden = false;
      inner.scrollTop = 0;
      announce(data.status);
    };

    const key = (query) => `${mode}\u0000${base}\u0000${query.toLowerCase()}`;
    const url = (query) => {
      const q = encodeURIComponent(query);
      return mode === 'predictive'
        ? `${base}/suggest?q=${q}&${PREDICTIVE}&section_id=${SECTION}`
        : `${base}?q=${q}&${FULL}&section_id=${SECTION}`;
    };

    const abort = () => {
      if (controller) controller.abort();
      controller = null;
    };

    const request = async (query) => {
      abort();
      const own = new AbortController();
      controller = own;
      const init = { signal: own.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } };
      let res = await fetch(url(query), init);
      if (res.status === 417 && mode === 'predictive') {
        mode = 'search';
        res = await fetch(url(query), init);
      }
      if (!res.ok) throw new Error(`mn-suggest: HTTP ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const node = doc.querySelector('[data-mn-ps-results]');
      if (!node) throw new Error('mn-suggest: no results');
      const data = {
        node,
        count: parseInt(node.getAttribute('data-mn-ps-count') || '0', 10) || 0,
        status: node.getAttribute('data-mn-ps-status') || '',
      };
      cache.set(key(query), data);
      if (cache.size > CACHE_SIZE) cache.delete(cache.keys().next().value);
      if (controller === own) controller = null;
      return data;
    };

    const update = () => {
      const query = input.value.trim();
      window.clearTimeout(timer);
      abort();
      setActive(-1);
      if (query.length < MIN_CHARS) {
        panel.classList.remove('is-loading');
        clear();
        return;
      }
      const cached = cache.get(key(query));
      if (cached) {
        render(cached, query);
        return;
      }
      panel.classList.add('is-loading');
      timer = window.setTimeout(() => {
        request(query)
          .then((data) => render(data, query))
          .catch((err) => {
            if (err && err.name === 'AbortError') return;
            panel.classList.remove('is-loading');
          });
      }, DEBOUNCE_MS);
    };

    input.addEventListener('input', update);
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= MIN_CHARS && panel.hidden) update();
    });

    input.addEventListener('keydown', (e) => {
      if (e.isComposing) return;
      const count = options.length;
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          if (!count) return;
          e.preventDefault();
          if (panel.hidden) {
            panel.hidden = false;
            input.setAttribute('aria-expanded', 'true');
          }
          const down = e.key === 'ArrowDown';
          let next;
          if (down) next = active + 1 >= count ? 0 : active + 1;
          else next = active <= 0 ? count - 1 : active - 1;
          setActive(next);
          break;
        }
        case 'Enter': {
          const option = options[active];
          if (!option || panel.hidden) return;
          e.preventDefault();
          option.click();
          break;
        }
        case 'Escape':
          if (!panel.hidden) {
            hide();
            if (floating) e.preventDefault();
          }
          break;
        default:
      }
    });

    form.addEventListener('submit', (e) => {
      if (!input.value.trim()) {
        e.preventDefault();
        input.focus();
      }
    });

    // Hide when the focus or a click goes elsewhere.
    const inside = (node) => node instanceof Node && (form.contains(node) || panel.contains(node));
    d.addEventListener('pointerdown', (e) => {
      if (!panel.hidden && !inside(e.target)) hide();
    }, true);
    form.addEventListener('focusout', (e) => {
      if (!inside(e.relatedTarget)) hide();
    });

    // One highlight: the mouse follows the keyboard; pressing an option keeps the focus in the field.
    panel.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      const option = e.target instanceof Element ? e.target.closest('[role="option"]') : null;
      const index = option ? options.indexOf(option) : -1;
      if (index !== -1 && index !== active) setActive(index, false);
    });
    panel.addEventListener('pointerdown', (e) => {
      // Keep the focus in the field (links still open on click; touch scrolling is not affected).
      if (e.target instanceof Element && !e.target.closest('input, button, select, textarea')) e.preventDefault();
    });
  };

  const init = (root) => {
    (root.querySelectorAll ? root : d).querySelectorAll(SELECTOR).forEach(enhance);
  };

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', () => init(d));
  else init(d);
  d.addEventListener('shopify:section:load', (e) => init(e.target));
})();
